/**
 * Undo / redo for the editor (desktop keys, toolbar, VR menus).
 *
 * zundo keeps whole snapshots of the world data, which is fine for one
 * builder but not with others editing live. Two rules make it safe:
 *
 *  - Edits arriving from the network (withRemoteOrigin) are not steps of your
 *    own; instead they are patched into every saved snapshot, so stepping back
 *    later only reverts YOUR changes and never resurrects or deletes theirs.
 *  - An undo is a real edit: primitives, lights and content are broadcast and
 *    written through here; environment models, skyboxes, screen props and
 *    world settings follow automatically via worldSync's store subscription.
 *
 * History is cleared once a world has loaded (clearWorldHistory), so undo can
 * never step back past the load into an empty world.
 */
import { TRACKED_FIELDS, useWorldStore } from '../stores/worldStore';
import { broadcastObjectAdd, broadcastObjectDelete, broadcastObjectUpdate } from './worldBroadcast';

type Data = Record<string, any>;

const OBJECT_MAPS = new Set(['environmentModels', 'contentItems', 'screenProps', 'primitives', 'lights', 'skyboxes']);
// Kinds that worldSync does not broadcast by itself.
const BROADCAST = { primitives: 'primitive', lights: 'light', contentItems: 'content' } as const;

const tracked = (): Data => {
  const s = useWorldStore.getState() as any;
  const out: Data = {};
  for (const key of TRACKED_FIELDS) out[key] = s[key];
  return out;
};

/** Run network-originated store changes without recording them as your steps. */
export function applyOutsideHistory(fn: () => void) {
  const t = useWorldStore.temporal.getState();
  const wasTracking = t.isTracking;
  const before = tracked();
  if (wasTracking) t.pause();
  try {
    fn();
  } finally {
    if (wasTracking) useWorldStore.temporal.getState().resume();
    patchHistory(before, tracked());
  }
}

// Echoes of your own writes come back as new objects with the same content;
// they are not someone else's change and must not touch the history.
const same = (x: unknown, y: unknown) => x === y || JSON.stringify(x) === JSON.stringify(y);

function patchHistory(before: Data, after: Data) {
  const changes: Array<(s: Data) => void> = [];
  for (const key of TRACKED_FIELDS) {
    const a = before[key], b = after[key];
    if (a === b) continue;
    if (OBJECT_MAPS.has(key) && a && b) {
      for (const id of new Set([...Object.keys(a), ...Object.keys(b)])) {
        if (same(a[id], b[id])) continue;
        changes.push((s) => {
          const m = { ...(s[key] || {}) };
          if (id in b) m[id] = b[id]; else delete m[id];
          s[key] = m;
        });
      }
    } else if (!same(a, b)) {
      changes.push((s) => { s[key] = b; });
    }
  }
  if (!changes.length) return;
  const t = useWorldStore.temporal.getState();
  if (!t.pastStates.length && !t.futureStates.length) return;
  const patch = (st: Data) => { const s = { ...st }; for (const c of changes) c(s); return s; };
  useWorldStore.temporal.setState({
    pastStates: t.pastStates.map(patch as any),
    futureStates: t.futureStates.map(patch as any),
  });
}

/** Forget history — call once a world has finished loading. */
export function clearWorldHistory() {
  useWorldStore.temporal.getState().clear();
}

function step(dir: 'undo' | 'redo', worldId?: string | null): boolean {
  const t = useWorldStore.temporal.getState();
  if (!(dir === 'undo' ? t.pastStates.length : t.futureStates.length)) return false;
  const before = tracked();
  if (dir === 'undo') t.undo(); else t.redo();
  const after = tracked();
  if (worldId) {
    for (const [key, type] of Object.entries(BROADCAST)) {
      const a = before[key] || {}, b = after[key] || {};
      for (const id of Object.keys(b)) {
        if (!a[id]) broadcastObjectAdd(worldId, type, id, b[id]);
        else if (a[id] !== b[id]) broadcastObjectUpdate(worldId, type, id, b[id]);
      }
      for (const id of Object.keys(a)) if (!b[id]) broadcastObjectDelete(worldId, type, id);
    }
  }
  // Drop a selection that no longer exists.
  const s = useWorldStore.getState();
  const exists = (id: string) => !!(s.primitives[id] || s.lights[id] || s.contentItems[id] || s.environmentModels[id] || s.screenProps[id]);
  if (s.selectedObjectId && !exists(s.selectedObjectId)) s.setSelectedObjectId(null);
  return true;
}

export const undoWorld = (worldId?: string | null) => step('undo', worldId);
export const redoWorld = (worldId?: string | null) => step('redo', worldId);
